import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Link2, Copy, QrCode, BarChart3, ExternalLink, Plus, Trash2, MousePointer } from 'lucide-react';
import { useSEO } from '../hooks/useSEO';
import { toast } from 'react-hot-toast';
import AdContainer from '../components/ads/AdContainer';
import { GOOGLE_ADSENSE_CONFIG } from '../config/adsConfig';

interface ShortenedUrl {
  id: string;
  originalUrl: string;
  shortUrl: string;
  customSlug?: string;
  title: string;
  createdAt: number;
  expiresAt?: number;
  clicks: number;
  isActive: boolean;
  qrCode?: string;
}

interface ClickAnalytics {
  totalClicks: number;
  uniqueClicks: number;
  clicksByCountry: Array<{ country: string; clicks: number }>;
  clicksByDate: Array<{ date: string; clicks: number }>;
  referrers: Array<{ source: string; clicks: number }>;
  devices: Array<{ device: string; clicks: number }>;
}

const URLShortener: React.FC = () => {
  const [originalUrl, setOriginalUrl] = useState('');
  const [customSlug, setCustomSlug] = useState('');
  const [title, setTitle] = useState('');
  const [expirationDays, setExpirationDays] = useState<number | null>(null);
  const [isCreating, setIsCreating] = useState(false);
  const [shortenedUrls, setShortenedUrls] = useState<ShortenedUrl[]>([]);
  const [selectedUrl, setSelectedUrl] = useState<ShortenedUrl | null>(null);
  const [analytics, setAnalytics] = useState<ClickAnalytics | null>(null);
  const [showQrCode, setShowQrCode] = useState<string | null>(null);

  useSEO({
    title: 'URL Shortener - Create Short Links with Analytics',
    description: 'Free URL shortener with click tracking, QR codes, and analytics. Create branded short links with custom domains and detailed statistics.',
    keywords: 'URL shortener, link shortener, short links, QR code generator, link analytics, click tracking, branded links',
    canonical: 'https://snap-tools.online/seo/url-shortener'
  });

  // Generate random short code
  const generateShortCode = (length: number = 6): string => {
    const chars = 'abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
    let result = '';
    for (let i = 0; i < length; i++) {
      result += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    return result;
  };

  // Generate QR Code (mock implementation)
  const generateQrCode = (url: string): string => {
    // In a real implementation, you would use a QR code library
    return `https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodeURIComponent(url)}`;
  };

  // Create shortened URL
  const createShortUrl = async () => {
    if (!originalUrl) {
      toast.error('Please enter a URL to shorten');
      return;
    }

    // Validate URL
    try {
      new URL(originalUrl);
    } catch {
      toast.error('Please enter a valid URL');
      return;
    }

    // Check for duplicate custom slug
    if (customSlug && shortenedUrls.some(url => url.customSlug === customSlug)) {
      toast.error('Custom slug already exists');
      return;
    }

    setIsCreating(true);

    try {
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 1000));

      const shortCode = customSlug || generateShortCode();
      const shortUrl = `https://snap.ly/${shortCode}`;
      const qrCode = generateQrCode(shortUrl);

      const newUrl: ShortenedUrl = {
        id: Math.random().toString(36).substr(2, 9),
        originalUrl,
        shortUrl,
        customSlug: customSlug || undefined,
        title: title || new URL(originalUrl).hostname,
        createdAt: Date.now(),
        expiresAt: expirationDays ? Date.now() + (expirationDays * 24 * 60 * 60 * 1000) : undefined,
        clicks: 0,
        isActive: true,
        qrCode
      };

      setShortenedUrls(prev => [newUrl, ...prev]);
      
      // Reset form
      setOriginalUrl('');
      setCustomSlug('');
      setTitle('');
      setExpirationDays(null);

      toast.success('Short URL created successfully!');
    } catch (error) {
      console.error('Failed to create short URL:', error);
      toast.error('Failed to create short URL');
    } finally {
      setIsCreating(false);
    }
  };

  // Copy URL to clipboard
  const copyToClipboard = async (url: string) => {
    try {
      await navigator.clipboard.writeText(url);
      toast.success('Copied to clipboard!');
    } catch (error) {
      console.error('Failed to copy:', error);
      toast.error('Failed to copy URL');
    }
  };

  // Delete shortened URL
  const deleteUrl = (id: string) => {
    setShortenedUrls(prev => prev.filter(url => url.id !== id));
    toast.success('URL deleted successfully');
  };

  // Toggle URL active status
  const toggleUrlStatus = (id: string) => {
    setShortenedUrls(prev => prev.map(url => 
      url.id === id ? { ...url, isActive: !url.isActive } : url
    ));
  };

  // View analytics
  const viewAnalytics = async (url: ShortenedUrl) => {
    setSelectedUrl(url);
    
    // Simulate analytics data
    const mockAnalytics: ClickAnalytics = {
      totalClicks: Math.floor(Math.random() * 1000),
      uniqueClicks: Math.floor(Math.random() * 500),
      clicksByCountry: [
        { country: 'United States', clicks: Math.floor(Math.random() * 300) },
        { country: 'United Kingdom', clicks: Math.floor(Math.random() * 150) },
        { country: 'Germany', clicks: Math.floor(Math.random() * 100) },
        { country: 'France', clicks: Math.floor(Math.random() * 80) },
        { country: 'Canada', clicks: Math.floor(Math.random() * 60) }
      ],
      clicksByDate: Array.from({ length: 7 }, (_, i) => ({
        date: new Date(Date.now() - i * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
        clicks: Math.floor(Math.random() * 50)
      })).reverse(),
      referrers: [
        { source: 'Direct', clicks: Math.floor(Math.random() * 200) },
        { source: 'Google', clicks: Math.floor(Math.random() * 150) },
        { source: 'Facebook', clicks: Math.floor(Math.random() * 100) },
        { source: 'Twitter', clicks: Math.floor(Math.random() * 80) },
        { source: 'LinkedIn', clicks: Math.floor(Math.random() * 60) }
      ],
      devices: [
        { device: 'Mobile', clicks: Math.floor(Math.random() * 300) },
        { device: 'Desktop', clicks: Math.floor(Math.random() * 200) },
        { device: 'Tablet', clicks: Math.floor(Math.random() * 100) }
      ]
    };

    setAnalytics(mockAnalytics);
  };

  // Load example
  const loadExample = () => {
    setOriginalUrl('https://www.example.com/very-long-url-that-needs-to-be-shortened');
    setTitle('Example Website');
    toast.success('Example URL loaded');
  };

  // Format date
  const formatDate = (timestamp: number) => {
    return new Date(timestamp).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 to-indigo-100 py-8">
      <div className="container mx-auto px-4 max-w-6xl">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="bg-white rounded-2xl shadow-xl p-6 md:p-8"
        >
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-purple-100 rounded-full mb-4">
              <Link2 className="w-8 h-8 text-purple-600" />
            </div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">URL Shortener</h1>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Create short, branded links with detailed analytics. Track clicks, generate QR codes, and manage your URLs with custom domains.
            </p>
          </div>

          <AdContainer 
            position="header"
            adSlot={GOOGLE_ADSENSE_CONFIG.adSlots.imageConverter.header}
            className="mb-6"
          />

          {/* URL Creation Form */}
          <div className="bg-gray-50 rounded-lg p-6 mb-8">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Create Short URL</h3>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Original URL *
                </label>
                <div className="flex gap-3">
                  <input
                    type="url"
                    value={originalUrl}
                    onChange={(e) => setOriginalUrl(e.target.value)}
                    placeholder="https://example.com/your-long-url"
                    className="flex-1 p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  />
                  <button
                    onClick={loadExample}
                    className="bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 transition-colors"
                  >
                    Example
                  </button>
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Custom Slug (optional)
                  </label>
                  <div className="flex">
                    <span className="inline-flex items-center px-3 rounded-l-lg border border-r-0 border-gray-300 bg-gray-50 text-gray-500 text-sm">
                      snap.ly/
                    </span>
                    <input
                      type="text"
                      value={customSlug}
                      onChange={(e) => setCustomSlug(e.target.value.replace(/[^a-zA-Z0-9-_]/g, ''))}
                      placeholder="my-custom-link"
                      className="flex-1 p-3 border border-gray-300 rounded-r-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Title (optional)
                  </label>
                  <input
                    type="text"
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    placeholder="Descriptive title for your link"
                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  />
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-4 items-end">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Expiration (optional)
                  </label>
                  <select
                    value={expirationDays || ''}
                    onChange={(e) => setExpirationDays(e.target.value ? parseInt(e.target.value) : null)}
                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  >
                    <option value="">No expiration</option>
                    <option value="1">1 day</option>
                    <option value="7">7 days</option>
                    <option value="30">30 days</option>
                    <option value="90">90 days</option>
                    <option value="365">1 year</option>
                  </select>
                </div>

                <button
                  onClick={createShortUrl}
                  disabled={isCreating || !originalUrl}
                  className="flex items-center justify-center gap-2 bg-purple-600 text-white px-6 py-3 rounded-lg hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  {isCreating ? (
                    <>Creating...</>
                  ) : (
                    <>
                      <Plus className="w-4 h-4" />
                      Create Short URL
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>

          {/* URL List */}
          {shortenedUrls.length > 0 && (
            <div className="bg-white border border-gray-200 rounded-lg p-6 mb-8">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Your Short URLs</h3>
              
              <div className="space-y-4">
                {shortenedUrls.map((url) => (
                  <div key={url.id} className="border border-gray-200 rounded-lg p-4">
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <h4 className="font-medium text-gray-900 truncate">{url.title}</h4>
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                            url.isActive ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                          }`}>
                            {url.isActive ? 'Active' : 'Inactive'}
                          </span>
                        </div>
                        
                        <div className="space-y-1">
                          <div className="flex items-center gap-2">
                            <span className="text-sm font-medium text-blue-600">{url.shortUrl}</span>
                            <button
                              onClick={() => copyToClipboard(url.shortUrl)}
                              className="p-1 text-gray-400 hover:text-gray-600"
                            >
                              <Copy className="w-4 h-4" />
                            </button>
                          </div>
                          <div className="text-sm text-gray-500 truncate">{url.originalUrl}</div>
                          <div className="flex items-center gap-4 text-xs text-gray-400">
                            <span>Created: {formatDate(url.createdAt)}</span>
                            {url.expiresAt && (
                              <span>Expires: {formatDate(url.expiresAt)}</span>
                            )}
                            <span className="flex items-center gap-1">
                              <MousePointer className="w-3 h-3" />
                              {url.clicks} clicks
                            </span>
                          </div>
                        </div>
                      </div>

                      <div className="flex items-center gap-2 ml-4">
                        <button
                          onClick={() => setShowQrCode(url.qrCode || null)}
                          className="p-2 text-gray-400 hover:text-gray-600"
                          title="Show QR Code"
                        >
                          <QrCode className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => viewAnalytics(url)}
                          className="p-2 text-gray-400 hover:text-gray-600"
                          title="View Analytics"
                        >
                          <BarChart3 className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => toggleUrlStatus(url.id)}
                          className={`p-2 rounded ${
                            url.isActive 
                              ? 'text-green-600 hover:bg-green-50' 
                              : 'text-red-600 hover:bg-red-50'
                          }`}
                          title={url.isActive ? 'Deactivate' : 'Activate'}
                        >
                          <ExternalLink className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => deleteUrl(url.id)}
                          className="p-2 text-red-400 hover:text-red-600 hover:bg-red-50 rounded"
                          title="Delete"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* QR Code Modal */}
          {showQrCode && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
              <div className="bg-white rounded-lg p-6 max-w-sm mx-4">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">QR Code</h3>
                <div className="text-center">
                  <img src={showQrCode} alt="QR Code" className="mx-auto mb-4" />
                  <p className="text-sm text-gray-600 mb-4">
                    Scan this QR code to access the short URL
                  </p>
                  <button
                    onClick={() => setShowQrCode(null)}
                    className="bg-purple-600 text-white px-4 py-2 rounded-lg hover:bg-purple-700 transition-colors"
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Analytics Modal */}
          {selectedUrl && analytics && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
              <div className="bg-white rounded-lg p-6 max-w-4xl w-full max-h-[90vh] overflow-y-auto">
                <div className="flex items-center justify-between mb-6">
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">{selectedUrl.title}</h3>
                    <p className="text-sm text-gray-600">{selectedUrl.shortUrl}</p>
                  </div>
                  <button
                    onClick={() => {
                      setSelectedUrl(null);
                      setAnalytics(null);
                    }}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    ×
                  </button>
                </div>

                {/* Analytics Overview */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                  <div className="bg-blue-50 rounded-lg p-4">
                    <div className="text-2xl font-bold text-blue-600">{analytics.totalClicks}</div>
                    <div className="text-sm text-blue-700">Total Clicks</div>
                  </div>
                  <div className="bg-green-50 rounded-lg p-4">
                    <div className="text-2xl font-bold text-green-600">{analytics.uniqueClicks}</div>
                    <div className="text-sm text-green-700">Unique Clicks</div>
                  </div>
                  <div className="bg-purple-50 rounded-lg p-4">
                    <div className="text-2xl font-bold text-purple-600">
                      {((analytics.uniqueClicks / analytics.totalClicks) * 100).toFixed(1)}%
                    </div>
                    <div className="text-sm text-purple-700">Unique Rate</div>
                  </div>
                  <div className="bg-yellow-50 rounded-lg p-4">
                    <div className="text-2xl font-bold text-yellow-600">
                      {formatDate(selectedUrl.createdAt)}
                    </div>
                    <div className="text-sm text-yellow-700">Created</div>
                  </div>
                </div>

                {/* Analytics Details */}
                <div className="grid md:grid-cols-2 gap-6">
                  <div className="bg-gray-50 rounded-lg p-4">
                    <h4 className="font-semibold text-gray-900 mb-3">Top Countries</h4>
                    <div className="space-y-2">
                      {analytics.clicksByCountry.map((country, index) => (
                        <div key={index} className="flex justify-between">
                          <span className="text-sm">{country.country}</span>
                          <span className="text-sm font-medium">{country.clicks}</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="bg-gray-50 rounded-lg p-4">
                    <h4 className="font-semibold text-gray-900 mb-3">Top Referrers</h4>
                    <div className="space-y-2">
                      {analytics.referrers.map((referrer, index) => (
                        <div key={index} className="flex justify-between">
                          <span className="text-sm">{referrer.source}</span>
                          <span className="text-sm font-medium">{referrer.clicks}</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="bg-gray-50 rounded-lg p-4">
                    <h4 className="font-semibold text-gray-900 mb-3">Device Types</h4>
                    <div className="space-y-2">
                      {analytics.devices.map((device, index) => (
                        <div key={index} className="flex justify-between">
                          <span className="text-sm">{device.device}</span>
                          <span className="text-sm font-medium">{device.clicks}</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="bg-gray-50 rounded-lg p-4">
                    <h4 className="font-semibold text-gray-900 mb-3">Clicks by Date</h4>
                    <div className="space-y-2">
                      {analytics.clicksByDate.slice(-5).map((day, index) => (
                        <div key={index} className="flex justify-between">
                          <span className="text-sm">{new Date(day.date).toLocaleDateString()}</span>
                          <span className="text-sm font-medium">{day.clicks}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          <AdContainer 
            position="footer"
            adSlot={GOOGLE_ADSENSE_CONFIG.adSlots.imageConverter.footer}
            className="mb-6"
          />

          {/* Educational Content */}
          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-blue-50 rounded-lg p-6">
              <h4 className="font-semibold text-blue-900 mb-3">URL Shortener Benefits</h4>
              <ul className="text-sm text-blue-800 space-y-2">
                <li>• <strong>Better Aesthetics:</strong> Clean, professional-looking links</li>
                <li>• <strong>Social Media:</strong> Save characters in Twitter and SMS</li>
                <li>• <strong>Click Tracking:</strong> Monitor engagement and performance</li>
                <li>• <strong>Brand Recognition:</strong> Use custom domains for branding</li>
                <li>• <strong>Link Management:</strong> Update destinations without changing links</li>
                <li>• <strong>Campaign Tracking:</strong> Analyze marketing campaign effectiveness</li>
              </ul>
            </div>

            <div className="bg-green-50 rounded-lg p-6">
              <h4 className="font-semibold text-green-900 mb-3">Best Practices</h4>
              <ul className="text-sm text-green-800 space-y-2">
                <li>• <strong>Custom Slugs:</strong> Use descriptive, memorable URLs</li>
                <li>• <strong>Link Testing:</strong> Always test links before sharing</li>
                <li>• <strong>Expiration Dates:</strong> Set expiry for time-sensitive content</li>
                <li>• <strong>Analytics Review:</strong> Regularly check performance metrics</li>
                <li>• <strong>Security:</strong> Monitor for malicious use of your links</li>
                <li>• <strong>Backup Plans:</strong> Have alternative links for important content</li>
              </ul>
            </div>

            <div className="bg-purple-50 rounded-lg p-6">
              <h4 className="font-semibold text-purple-900 mb-3">Marketing Applications</h4>
              <ul className="text-sm text-purple-800 space-y-2">
                <li>• <strong>Social Media:</strong> Track clicks from different platforms</li>
                <li>• <strong>Email Campaigns:</strong> Monitor email click-through rates</li>
                <li>• <strong>Print Media:</strong> Use QR codes for offline-to-online bridge</li>
                <li>• <strong>A/B Testing:</strong> Compare different link strategies</li>
                <li>• <strong>Affiliate Marketing:</strong> Mask affiliate links professionally</li>
                <li>• <strong>Event Promotion:</strong> Create memorable event links</li>
              </ul>
            </div>

            <div className="bg-yellow-50 rounded-lg p-6">
              <h4 className="font-semibold text-yellow-900 mb-3">Analytics Insights</h4>
              <ul className="text-sm text-yellow-800 space-y-2">
                <li>• <strong>Geographic Data:</strong> Understand your global reach</li>
                <li>• <strong>Time Patterns:</strong> Identify peak engagement times</li>
                <li>• <strong>Referrer Sources:</strong> See which platforms drive traffic</li>
                <li>• <strong>Device Analytics:</strong> Optimize for mobile or desktop</li>
                <li>• <strong>Click Velocity:</strong> Track viral content spread</li>
                <li>• <strong>Conversion Tracking:</strong> Measure campaign ROI</li>
              </ul>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default URLShortener;
